import { Topbar } from '@/components/organisms/Topbar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';

/** A white card, as on the loaded page, holding grey bars where its content will be. */
function SkeletonCard({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        background: 'var(--paper)',
        borderRadius: 7,
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** One "label … value" line, as in the history and setup lists. */
function SkeletonListRow() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
      <Skeleton className="h-3.5 flex-1" />
      <Skeleton className="h-3 w-14" />
    </div>
  );
}

/**
 * Detail-page skeleton shown while the overview query is pending. It mirrors
 * the loaded Overview tab block for block — header identity, tab bar, the row
 * of three cards, then the chart beside the setup card — on the same grid and
 * breakpoints, so nothing jumps when the content arrives.
 */
export function DetailLoadingState() {
  return (
    <>
      <Topbar
        crumbs={[{ label: 'Email accounts', to: '/email-accounts' }, { label: 'Loading…' }]}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Skeleton className="h-[42px] w-[42px] shrink-0 rounded-[10px]" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Skeleton className="h-6 w-64 max-w-[50vw]" />
                <Skeleton className="h-[22px] w-20 rounded-full" />
              </div>
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        }
        actions={
          <>
            <Skeleton className="h-9 w-32 rounded-full" />
            <Skeleton className="h-9 w-9" />
          </>
        }
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 26,
          minHeight: 44,
          padding: '0 28px',
          background: 'var(--paper)',
          borderBottom: '1px solid var(--ink-200)',
        }}
      >
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3.5 w-16" />
        <Skeleton className="ml-auto h-3.5 w-28" />
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 28px 28px',
          background: 'var(--canvas)',
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <span className="sr-only">Loading account overview…</span>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 min-[1180px]:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_clamp(340px,32%,460px)]">
            {/* Reputation score: label, big number + badge, scale, sentence row. */}
            <SkeletonCard>
              <Skeleton className="h-3.5 w-28" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Skeleton className="h-11 w-20" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-2 w-full" />
              <Skeleton className="mt-auto h-[58px] w-full" />
            </SkeletonCard>
            {/* Last 15 days: label, 2 x 2 figures, Analytics row. */}
            <SkeletonCard style={{ gap: 16 }}>
              <Skeleton className="h-3.5 w-24" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 24px' }}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-7 w-14" />
                  </div>
                ))}
              </div>
              <Skeleton className="mt-auto h-[58px] w-full" />
            </SkeletonCard>
            {/* Recent mailbox history: label, three events, activity row. */}
            <SkeletonCard>
              <Skeleton className="h-3.5 w-40" />
              <SkeletonListRow />
              <SkeletonListRow />
              <SkeletonListRow />
              <Skeleton className="mt-auto h-[58px] w-full" />
            </SkeletonCard>
          </div>

          <div className="grid grid-cols-1 items-stretch gap-4 min-[1180px]:grid-cols-[minmax(0,1fr)_clamp(340px,32%,460px)]">
            {/* Reputation over time: title, chip + sentence, the chart. */}
            <SkeletonCard>
              <Skeleton className="h-4 w-56" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Skeleton className="h-[22px] w-16 rounded-full" />
                <Skeleton className="h-3.5 w-72 max-w-[60%]" />
              </div>
              <Skeleton className="h-[300px] w-full" />
            </SkeletonCard>
            {/* Setup & authentication: title + Re-check, three groups of rows, footer. */}
            <SkeletonCard style={{ gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-8 w-24" />
              </div>
              {[4, 1, 2].map((rows, group) => (
                <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Skeleton className="h-2.5 w-24" />
                  {Array.from({ length: rows }, (_, i) => (
                    <SkeletonListRow key={i} />
                  ))}
                </div>
              ))}
              <Skeleton className="mt-auto h-3 w-44" />
            </SkeletonCard>
          </div>
        </div>
      </div>
    </>
  );
}

/** Detail-page error state with Retry + Back-to-accounts actions. */
export function DetailErrorState({
  error,
  onRetry,
  onBack,
}: {
  error: unknown;
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <Topbar
        crumbs={[{ label: 'Email accounts', to: '/email-accounts' }]}
        title="Email account"
        subtitle="Couldn't load this account."
      />
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 28px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          background: 'var(--canvas)',
        }}
      >
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-destructive"
        >
          <p className="text-sm font-semibold">
            We couldn't load this email account.
          </p>
          <p className="mt-1 text-sm">
            {error instanceof ApiError ? error.message : 'A network error occurred.'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant="default" size="sm" onClick={onRetry}>
              Retry
            </Button>
            <Button variant="secondary" size="sm" onClick={onBack}>
              Back to accounts
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
